// Curves — Photoshop's spline-mapped per-channel input/output adjustment. The
// user defines a small set of control points; we interpolate between them
// with a monotone cubic spline (Fritsch-Carlson) and bake the result into a
// 256-entry LUT.
//
// Why Fritsch-Carlson and not Catmull-Rom or natural cubic? Photoshop's
// Curves is monotone between control points by construction — a user drags a
// midtone point up, every intermediate value goes up, no overshoot. Naive
// cubic splines can dip below the input on either side of a steep point,
// which produces visible posterization artifacts where the curve briefly
// reverses direction. Fritsch-Carlson preserves monotonicity by adjusting
// the tangents at each interior knot to never overshoot.
//
// Reference: Fritsch, F. N. & Carlson, R. E. (1980). "Monotone Piecewise
// Cubic Interpolation". SIAM J. Numer. Anal. 17(2): 238-246.
//
// Alpha is never touched. Selection masking is delegated to
// `applyFilterInPlace`.

import { PixelBuffer } from "../pixel/PixelBuffer";
import type { Selection } from "../selection/Selection";
import type { Filter } from "./Filter";

export interface CurvesPoint {
  /** 0..255. */
  input: number;
  /** 0..255. */
  output: number;
}

export type CurvesChannel = "rgb" | "r" | "g" | "b";

export interface CurvesParams {
  channel: CurvesChannel;
  /** At least two points. Sorted by `input` ascending; we re-sort defensively. */
  points: ReadonlyArray<CurvesPoint>;
}

const DEFAULT_PARAMS: CurvesParams = {
  channel: "rgb",
  points: [
    { input: 0, output: 0 },
    { input: 255, output: 255 },
  ],
};

/**
 * Build a 256-entry LUT from the given control points using monotone cubic
 * interpolation (Fritsch-Carlson). Points are sorted defensively and
 * duplicates on `input` collapse to the later entry.
 *
 * Exported for testing.
 */
export function buildCurvesLut(
  points: ReadonlyArray<CurvesPoint>,
): Uint8ClampedArray {
  // Defensive: sort by input, deduplicate.
  const sorted = [...points].sort((a, b) => a.input - b.input);
  const xs: number[] = [];
  const ys: number[] = [];
  for (const p of sorted) {
    const x = clamp255(p.input);
    const y = clamp255(p.output);
    if (xs.length > 0 && xs[xs.length - 1] === x) {
      // Duplicate x: take the later y (matches "last write wins").
      ys[ys.length - 1] = y;
    } else {
      xs.push(x);
      ys.push(y);
    }
  }

  const lut = new Uint8ClampedArray(256);

  if (xs.length === 0) {
    // Degenerate: identity.
    for (let i = 0; i < 256; i++) lut[i] = i;
    return lut;
  }
  if (xs.length === 1) {
    // Single point: flat at that y.
    const y = ys[0] ?? 0;
    for (let i = 0; i < 256; i++) lut[i] = y;
    return lut;
  }

  // Fritsch-Carlson tangent computation.
  //
  // 1. Slopes of secants:    d_k = (y_{k+1} - y_k) / (x_{k+1} - x_k)
  // 2. Initial tangents:     m_k = (d_{k-1} + d_k) / 2  for interior k
  //                           m_0 = d_0,  m_{n-1} = d_{n-2}
  // 3. Monotonicity fix:     if d_k == 0, set m_k = m_{k+1} = 0
  //                           else let a = m_k / d_k, b = m_{k+1} / d_k
  //                                if a*a + b*b > 9, scale m_k and m_{k+1}
  //                                by 3/sqrt(a*a + b*b) to clip to the circle.
  const n = xs.length;
  const d: number[] = new Array(n - 1);
  for (let k = 0; k < n - 1; k++) {
    const dx = (xs[k + 1] ?? 0) - (xs[k] ?? 0);
    const dy = (ys[k + 1] ?? 0) - (ys[k] ?? 0);
    d[k] = dx === 0 ? 0 : dy / dx;
  }
  const m: number[] = new Array(n);
  m[0] = d[0] ?? 0;
  m[n - 1] = d[n - 2] ?? 0;
  for (let k = 1; k < n - 1; k++) {
    const dPrev = d[k - 1] ?? 0;
    const dCur = d[k] ?? 0;
    if (dPrev * dCur <= 0) {
      // Local extremum: tangent must be zero to preserve monotonicity.
      m[k] = 0;
    } else {
      m[k] = (dPrev + dCur) / 2;
    }
  }
  for (let k = 0; k < n - 1; k++) {
    const dk = d[k] ?? 0;
    if (dk === 0) {
      m[k] = 0;
      m[k + 1] = 0;
      continue;
    }
    const a = (m[k] ?? 0) / dk;
    const b = (m[k + 1] ?? 0) / dk;
    const s = a * a + b * b;
    if (s > 9) {
      const t = 3 / Math.sqrt(s);
      m[k] = t * a * dk;
      m[k + 1] = t * b * dk;
    }
  }

  // Evaluate the Hermite cubic on each segment for v=0..255.
  let seg = 0;
  for (let v = 0; v < 256; v++) {
    // Clamp v to the spline's x-range; outside, hold the endpoint y.
    if (v <= (xs[0] ?? 0)) {
      lut[v] = Math.round(ys[0] ?? 0);
      continue;
    }
    if (v >= (xs[n - 1] ?? 0)) {
      lut[v] = Math.round(ys[n - 1] ?? 0);
      continue;
    }
    // Advance the segment pointer monotonically — v is increasing.
    while (seg < n - 1 && v > (xs[seg + 1] ?? 0)) seg++;
    const x0 = xs[seg] ?? 0;
    const x1 = xs[seg + 1] ?? 0;
    const y0 = ys[seg] ?? 0;
    const y1 = ys[seg + 1] ?? 0;
    const m0 = m[seg] ?? 0;
    const m1 = m[seg + 1] ?? 0;
    const dx = x1 - x0;
    const t = dx === 0 ? 0 : (v - x0) / dx;
    // Cubic Hermite basis.
    const t2 = t * t;
    const t3 = t2 * t;
    const h00 = 2 * t3 - 3 * t2 + 1;
    const h10 = t3 - 2 * t2 + t;
    const h01 = -2 * t3 + 3 * t2;
    const h11 = t3 - t2;
    const y = h00 * y0 + h10 * dx * m0 + h01 * y1 + h11 * dx * m1;
    lut[v] = Math.round(y);
  }
  return lut;
}

function clamp255(v: number): number {
  if (!isFinite(v)) return 0;
  if (v < 0) return 0;
  if (v > 255) return 255;
  return v;
}

const IDENTITY_LUT: Uint8ClampedArray = (() => {
  const lut = new Uint8ClampedArray(256);
  for (let i = 0; i < 256; i++) lut[i] = i;
  return lut;
})();

export const Curves: Filter<CurvesParams> = {
  id: "curves",
  displayName: "Curves",
  defaultParams: DEFAULT_PARAMS,

  apply(
    input: PixelBuffer,
    params: CurvesParams,
    _selection: Selection | null,
  ): PixelBuffer {
    const w = input.width;
    const h = input.height;
    const out = new PixelBuffer(w, h, input.mode);
    const src = input.data;
    const dst = out.data;
    const n = w * h;

    const channel = params.channel;
    const channelLut = buildCurvesLut(params.points);

    const rLut = channel === "rgb" || channel === "r" ? channelLut : IDENTITY_LUT;
    const gLut = channel === "rgb" || channel === "g" ? channelLut : IDENTITY_LUT;
    const bLut = channel === "rgb" || channel === "b" ? channelLut : IDENTITY_LUT;

    for (let i = 0; i < n; i++) {
      const o = i * 4;
      dst[o] = rLut[src[o] ?? 0] ?? 0;
      dst[o + 1] = gLut[src[o + 1] ?? 0] ?? 0;
      dst[o + 2] = bLut[src[o + 2] ?? 0] ?? 0;
      dst[o + 3] = src[o + 3] ?? 0;
    }
    return out;
  },
};
